import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';

// 定义新的状态接口，用于显示/隐藏的附加参数
interface VisibleState {
  isVisible: boolean; // 控制显示/隐藏
  parameters?: any; // 附加参数，可根据需求扩展
}

interface IMState {
  visibleState: VisibleState; // 显示/隐藏对象状态
  unread: number; // 未读消息数
}

class IMStateManager {
  // 将 isVisible 改为存储对象的 BehaviorSubject
  private visibleState$ = new BehaviorSubject<VisibleState>({isVisible: false}); // 默认不可见
  private unread$ = new BehaviorSubject<number>(0); // 未读消息数

  // 用于资源清理
  private destroy$ = new Subject<void>();

  // 公开的状态流
  readonly visibleState = this.visibleState$.asObservable();
  readonly unread = this.unread$.asObservable();

  // 综合状态流（完整状态）
  readonly state$: Observable<IMState> = combineLatest([
    this.visibleState$,
    this.unread$,
  ]).pipe(
    map(([visibleState, unread]) => ({visibleState, unread})), // 合并为 IMState
    takeUntil(this.destroy$), // 自动清理订阅
  );

  constructor() {
    // 构造函数更加简洁
  }

  /**
   * 获取完整当前状态
   */
  getState(): IMState {
    return {
      visibleState: this.visibleState$.getValue(),
      unread: this.unread$.getValue(),
    };
  }

  /**
   * 设置显示/隐藏状态及附加参数
   * @param isVisible - 是否可见
   * @param parameters - 附加参数（可选）
   */
  setVisibleState(isVisible: boolean, parameters: Record<any, any> = {}): void {
    this.visibleState$.next({isVisible, parameters});
  }

  /**
   * 设置未读消息数
   * @param unread - 未读消息数
   */
  setUnreadCount(unread: number): void {
    this.unread$.next(unread);
  }

  /**
   * 销毁资源
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleState$.complete();
    this.unread$.complete();
  }
}

// 示例用法
export const imManager = new IMStateManager();
